class FreeGiftPromotion extends HTMLElement {
  constructor() {
    super();
    this.giftProductId = this.dataset.giftProductId;
    this.threshold = parseInt(this.dataset.threshold, 10);
    this.isAddingGift = false;
    this.isRemovingGift = false;
    this.updatePromotionTimeout = null;
    this.initialGiftKeys = this.dataset.initialGiftKeys
      ? this.dataset.initialGiftKeys.split('||').filter((key) => key.length)
      : [];
    this.initialNonGiftCount = parseInt(this.dataset.initialNonGiftCount || '0', 10);
    this.initialGiftLines = this.dataset.initialGiftLines
      ? this.dataset.initialGiftLines.split('||').filter((line) => line.length).map((line) => parseInt(line, 10))
      : [];
  }

  connectedCallback() {
    console.log('[FreeGiftPromotion] 🔵 Component MOUNTED', {
      timestamp: new Date().toISOString(),
      giftProductId: this.giftProductId,
      threshold: this.threshold
    });

    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      console.log('[FreeGiftPromotion] ⏳ Waiting for pubsub to be available...');
      setTimeout(() => this.connectedCallback(), 100);
      return;
    }
    
    this.handleInitialGiftState();
    
    // Component gets replaced on every cart update, so we only need to handle initial state
    // The fresh data comes from Liquid when the component is recreated
    console.log('[FreeGiftPromotion] 📊 Scheduling initial update with fresh Liquid data...');
    this.debouncedUpdatePromotion(200);
    
    // No need to subscribe to cart updates - component gets replaced on each update
    // This prevents duplicate event listeners and ensures fresh data from server
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        if (cartDrawer.classList.contains('active')) {
          console.log('[FreeGiftPromotion] 👁️ Cart drawer opened, scheduling update...');
          this.debouncedUpdatePromotion(300);
        }
      });
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      console.log('[FreeGiftPromotion] 👁️ MutationObserver attached to cart-drawer');
    } else {
      console.warn('[FreeGiftPromotion] ⚠️ cart-drawer element not found');
    }
  }

  debouncedUpdatePromotion(delay = 200) {
    if (this.updatePromotionTimeout) {
      console.log('[FreeGiftPromotion] ⏸️ Clearing previous debounced update');
      clearTimeout(this.updatePromotionTimeout);
    }
    console.log(`[FreeGiftPromotion] ⏱️ Scheduling update in ${delay}ms`);
    this.updatePromotionTimeout = setTimeout(() => {
      this.updatePromotion();
    }, delay);
  }

  handleInitialGiftState() {
    if (this.initialGiftLines.length && this.initialNonGiftCount === 0 && !this.isRemovingGift) {
      this.removeGiftLines(this.initialGiftLines);
      this.initialGiftLines = [];
      this.initialGiftKeys = [];
    }
  }

  disconnectedCallback() {
    console.log('[FreeGiftPromotion] 🔴 Component UNMOUNTED', {
      timestamp: new Date().toISOString(),
      giftProductId: this.giftProductId
    });
    
    // Clean up timeout if component is being replaced
    if (this.updatePromotionTimeout) {
      console.log('[FreeGiftPromotion] ⏹️ Clearing pending update timeout');
      clearTimeout(this.updatePromotionTimeout);
      this.updatePromotionTimeout = null;
    }
  }

  async getCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  async updatePromotion() {
    console.log('[FreeGiftPromotion] 🔄 updatePromotion() called', {
      timestamp: new Date().toISOString(),
      isAddingGift: this.isAddingGift,
      isRemovingGift: this.isRemovingGift,
      addingGiftFlag: this.dataset.addingGift,
      removingGiftFlag: this.dataset.removingGift
    });

    // Prevent concurrent updates
    if (this.isAddingGift || this.isRemovingGift || this.dataset.addingGift === 'true' || this.dataset.removingGift === 'true') {
      console.log('[FreeGiftPromotion] ⏸️ Update blocked - operation in progress');
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      console.warn('[FreeGiftPromotion] ⚠️ cart-drawer not found, aborting');
      return;
    }
    
    const progressBar = document.querySelector('free-gift-progress-bar');
    const giftSection = this.querySelector('free-gift-section');
    
    if (!progressBar && !giftSection) {
      console.warn('[FreeGiftPromotion] ⚠️ Progress bar and gift section not found, aborting');
      return;
    }

    // Use fresh data from Liquid (calculated on server when component was created)
    const cartTotal = parseInt(this.dataset.initialCartTotal || '0', 10);
    const hasGift = this.dataset.initialHasGift === 'true';
    const thresholdMet = this.dataset.initialEarned === 'true';
    const nonGiftItemCount = parseInt(this.dataset.initialNonGiftCount || '0', 10);
    const giftLineIndexes = this.initialGiftLines.length > 0 ? [...this.initialGiftLines] : [];
    const giftCount = giftLineIndexes.length;

    console.log('[FreeGiftPromotion] 📊 Using Liquid data', {
      cartTotal: cartTotal,
      giftCount: giftCount,
      hasGift: hasGift,
      nonGiftItemCount: nonGiftItemCount,
      threshold: this.threshold,
      thresholdMet: thresholdMet
    });

    // If no non-gift items, remove all gifts and hide promotion
    if (nonGiftItemCount === 0) {
      console.log('[FreeGiftPromotion] 🗑️ No non-gift items, removing all gifts');
      this.style.display = 'none';
      
      if (giftSection) {
        giftSection.style.display = 'none';
        giftSection.setAttribute('data-visible', 'false');
        giftSection.setAttribute('data-earned', 'false');
        giftSection.setAttribute('data-remaining', this.threshold);
      }
      if (progressBar) {
        progressBar.style.display = 'none';
        progressBar.setAttribute('data-cart-total', 0);
        progressBar.setAttribute('data-remaining', this.threshold);
        progressBar.setAttribute('data-progress', 0);
        progressBar.setAttribute('data-earned', 'false');
      }
      if (hasGift && giftLineIndexes.length && !this.isRemovingGift && this.dataset.removingGift !== 'true') {
        await this.removeGiftLines(giftLineIndexes);
      }
      return;
    }

    // Show the promotion component when cart has items
    this.style.display = '';

    const remaining = parseInt(this.dataset.initialRemaining || '0', 10);
    const progress = parseFloat(this.dataset.initialProgress || '0');

    if (progressBar) {
      progressBar.style.display = 'block';
      progressBar.setAttribute('data-cart-total', cartTotal);
      progressBar.setAttribute('data-threshold', this.threshold);
      progressBar.setAttribute('data-remaining', remaining);
      progressBar.setAttribute('data-progress', thresholdMet ? 100 : progress);
      progressBar.setAttribute('data-earned', thresholdMet ? 'true' : 'false');
    }

    if (giftSection) {
      giftSection.style.display = 'block';
      giftSection.setAttribute('data-visible', 'true');
      giftSection.setAttribute('data-earned', thresholdMet ? 'true' : 'false');
      giftSection.setAttribute('data-remaining', remaining);
    }

    // Handle gift addition/removal logic
    // CRITICAL: Trust Liquid data - if it says gift exists, don't try to add
    if (thresholdMet) {
      console.log('[FreeGiftPromotion] ✅ Threshold met!');
      // Threshold met - ensure exactly one gift exists
      if (giftCount === 0 && !hasGift) {
        // Liquid says no gift exists - safe to add
        console.log('[FreeGiftPromotion] 🎁 No gift found in Liquid data, will verify and add...');
        // Only add if not already adding
        if (!this.isAddingGift && this.dataset.addingGift !== 'true') {
          await this.addGiftToCart();
        }
      } else if (hasGift || giftCount > 0) {
        // Liquid says gift exists - trust it, don't add
        if (giftCount > 1 && !this.isRemovingGift && this.dataset.removingGift !== 'true') {
          console.log(`[FreeGiftPromotion] ⚠️ Multiple gifts found (${giftCount}), removing extras...`);
          // Multiple gifts, remove extras (keep the first one)
          const extrasToRemove = giftLineIndexes.slice(1); // Keep first, remove rest
          await this.removeGiftLines(extrasToRemove);
        } else if (giftCount === 1 || hasGift) {
          console.log('[FreeGiftPromotion] ✅ Perfect - gift exists (trusting Liquid data)');
        }
      }
    } else {
      console.log('[FreeGiftPromotion] ❌ Threshold not met');
      // Threshold not met - remove all gifts
      if (hasGift && giftLineIndexes.length && !this.isRemovingGift && this.dataset.removingGift !== 'true') {
        console.log('[FreeGiftPromotion] 🗑️ Removing all gifts (threshold not met)');
        await this.removeGiftLines(giftLineIndexes);
      }
    }
  }

  hideGiftItem(itemKey) {
    const giftItems = document.querySelectorAll(`[data-variant-id="${this.giftProductId}"]`);
    giftItems.forEach(item => {
      const row = item.closest('tr.cart-item, .cart-item');
      if (row) {
        row.style.display = 'none';
        row.classList.add('free-gift-item');
      }
    });
  }

  async addGiftToCart() {
    const lockKey = `free-gift-adding-${this.giftProductId}`;
    const lockTimestamp = sessionStorage.getItem(lockKey);
    
    // Check if another instance is already adding (persists across component replacement)
    if (lockTimestamp) {
      const lockAge = Date.now() - parseInt(lockTimestamp, 10);
      if (lockAge < 5000) { // Lock expires after 5 seconds
        console.warn('[FreeGiftPromotion] ⛔ BLOCKED: Another instance is adding gift (lock exists)');
        return;
      } else {
        // Lock expired, remove it
        sessionStorage.removeItem(lockKey);
      }
    }

    console.log('[FreeGiftPromotion] 🎁 addGiftToCart() called', {
      timestamp: new Date().toISOString(),
      isAddingGift: this.isAddingGift,
      isRemovingGift: this.isRemovingGift,
      addingGiftFlag: this.dataset.addingGift,
      removingGiftFlag: this.dataset.removingGift,
      liquidHasGift: this.dataset.initialHasGift
    });

    // Double-check we're not already adding or removing
    if (this.isAddingGift || this.isRemovingGift) {
      console.log('[FreeGiftPromotion] ⏸️ Add blocked - operation already in progress');
      return;
    }

    // Check if we're already in the process of adding (using data attribute as backup)
    if (this.dataset.addingGift === 'true' || this.dataset.removingGift === 'true') {
      console.log('[FreeGiftPromotion] ⏸️ Add blocked - flag set');
      return;
    }

    // CRITICAL: Check Liquid data first - if it says gift exists, trust it
    if (this.dataset.initialHasGift === 'true') {
      console.warn('[FreeGiftPromotion] ⛔ BLOCKED: Liquid data says gift already exists!');
      return;
    }

    // Set lock to prevent other instances from adding
    sessionStorage.setItem(lockKey, Date.now().toString());

    // CRITICAL: Verify gift doesn't already exist by checking cart RIGHT BEFORE adding
    console.log('[FreeGiftPromotion] 🔍 Final check: fetching cart before add...');
    const cart = await this.getCart();
    if (!cart || !cart.items) {
      console.warn('[FreeGiftPromotion] ⚠️ Failed to fetch cart for final check');
      return;
    }

    const existingGifts = cart.items.filter(item => {
      const itemVariantId = String(item.variant_id);
      const giftVariantId = String(this.giftProductId);
      const isFreeGift = itemVariantId === giftVariantId ||
        (item.properties && (item.properties._free_gift === 'true' || item.properties._custom_free_gift === 'true'));
      return isFreeGift;
    });

    console.log('[FreeGiftPromotion] 🔍 Final check result', {
      existingGiftCount: existingGifts.length,
      cartItemCount: cart.items.length
    });

    // ABSOLUTE CHECK: If ANY gift exists, DO NOT ADD
    if (existingGifts.length > 0) {
      console.warn('[FreeGiftPromotion] ⛔ BLOCKED: Gift already exists in cart! Count:', existingGifts.length);
      return;
    }

    // Set flags to prevent multiple additions
    this.isAddingGift = true;
    this.dataset.addingGift = 'true';
    console.log('[FreeGiftPromotion] ✅ All checks passed, adding gift to cart...');
    
    try {
      console.log('[FreeGiftPromotion] 📤 Sending add request...', {
        giftProductId: this.giftProductId
      });
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.giftProductId,
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_custom_free_gift': 'true'
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[FreeGiftPromotion] ✅ Gift added successfully!', {
          cartItemCount: data.items?.length || 0
        });
        
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          console.log('[FreeGiftPromotion] 🔄 Updating cart drawer...');
          await cartDrawerItems.onCartUpdate();
        }
        
        // Use a small delay to ensure DOM is updated before publishing
        setTimeout(() => {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-promotion', cartData: data });
          if (data.items) {
            const giftItems = data.items.filter(item => {
              const itemVariantId = String(item.variant_id);
              const giftVariantId = String(this.giftProductId);
              return itemVariantId === giftVariantId ||
                (item.properties && (item.properties._free_gift === 'true' || item.properties._custom_free_gift === 'true'));
            });
            giftItems.forEach(giftItem => {
              this.hideGiftItem(giftItem.key);
            });
          }
          const giftSection = this.querySelector('free-gift-section');
          if (giftSection) {
            giftSection.style.display = 'block';
            giftSection.setAttribute('data-visible', 'true');
          }
        }, 300);
      } else {
        const errorText = await response.text();
        console.error('Failed to add gift to cart, response:', errorText);
      }
    } catch (error) {
      console.error('[FreeGiftPromotion] ❌ Error adding gift to cart:', error);
      // Remove lock on error
      sessionStorage.removeItem(lockKey);
    } finally {
      console.log('[FreeGiftPromotion] 🧹 Cleaning up add flags...');
      this.isAddingGift = false;
      // Clear the flag after a delay to prevent rapid re-additions
      setTimeout(() => {
        delete this.dataset.addingGift;
        sessionStorage.removeItem(lockKey); // Remove lock
        console.log('[FreeGiftPromotion] ✅ Add flag and lock cleared');
      }, 500);
    }
  }

  async removeGiftLines(lineIndexes = []) {
    if (!lineIndexes.length || this.isRemovingGift || this.isAddingGift || this.dataset.removingGift === 'true') {
      return;
    }

    this.isRemovingGift = true;
    this.dataset.removingGift = 'true';
    try {
      // Verify gifts still exist before removing
      const cart = await this.getCart();
      if (!cart || !cart.items) {
        return;
      }

      // Filter to only remove lines that actually exist and are gifts
      const validLinesToRemove = [];
      lineIndexes.forEach(lineIndex => {
        const item = cart.items[lineIndex - 1]; // lineIndex is 1-based
        if (item) {
          const itemVariantId = String(item.variant_id);
          const giftVariantId = String(this.giftProductId);
          const isFreeGift = itemVariantId === giftVariantId ||
            (item.properties && (item.properties._free_gift === 'true' || item.properties._custom_free_gift === 'true'));
          if (isFreeGift) {
            validLinesToRemove.push(lineIndex);
          }
        }
      });

      if (!validLinesToRemove.length) {
        return;
      }

      // Remove from highest line index to lowest so subsequent lines remain valid
      const sortedLines = [...validLinesToRemove].sort((a, b) => b - a);
      for (const line of sortedLines) {
        const response = await fetch(`${routes.cart_change_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          console.error('Failed to remove gift from cart, response:', await response.text());
          break;
        }
      }

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        await cartDrawerItems.onCartUpdate();
      }
      
      // Use a small delay to ensure DOM is updated before publishing
      setTimeout(() => {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-promotion' });
      }, 300);
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    } finally {
      this.isRemovingGift = false;
      delete this.dataset.removingGift;
    }
  }
}

customElements.define('free-gift-promotion', FreeGiftPromotion);
